/**
 * Content Service
 * Connects to unified /api/content endpoint from backend
 * Fetches menu content for all pages (Homepage, About, Education, etc)
 */

const API_BASE = process.env.REACT_APP_API_BASE || 'https://permataharapansecondary.sch.id/api';

class ContentService {
  /**
   * Fetch published content for satu menu
   * GET /api/content?menu=homepage&published=true
   * 
   * @param {string} menu - 'homepage', 'about-us', 'education-program', etc
   * @param {boolean} published - only published/approved content
   * @returns {Promise<Array>} array of sections
   */
  async getMenuContent(menu, published = true) {
    if (!menu) throw new Error('Menu parameter required');

    try {
      const params = new URLSearchParams({
        menu: menu,
        published: String(published)
      });

      const url = `${API_BASE}/content?${params.toString()}`;
      console.log('[contentService] GET', url);

      const response = await fetch(url, {
        credentials: 'omit',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.ok) {
        throw new Error(result.message || 'API returned error');
      }

      // result.data is array of sections for this menu
      const sections = result.data || [];
      console.log(`[contentService] ✓ Got ${sections.length} sections for menu: ${menu}`);
      return sections;

    } catch (error) {
      console.error(`[contentService] getMenuContent error:`, error);
      throw error;
    }
  }

  /**
   * Fetch satu section spesifik
   * GET /api/content?menu=homepage&section=hero&published=true
   * 
   * @param {string} menu - menu name
   * @param {string} section - section key
   * @param {boolean} published - published only
   * @returns {Promise<Object>} section object
   */
  async getSection(menu, section, published = true) {
    if (!menu || !section) throw new Error('Menu and section required');

    try {
      const params = new URLSearchParams({
        menu: menu,
        section: section,
        published: String(published)
      });

      const url = `${API_BASE}/content?${params.toString()}`;
      console.log('[contentService] GET section:', url);

      const response = await fetch(url, {
        credentials: 'omit',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const result = await response.json();
      if (!result.ok) throw new Error(result.message);

      // Return first section (should be only one)
      const sections = result.data || [];
      if (sections.length === 0) {
        console.warn(`[contentService] No section found: ${menu}/${section}`);
        return null;
      }

      return sections[0];

    } catch (error) {
      console.error(`[contentService] getSection error:`, error);
      throw error;
    }
  }

  /**
   * Clear all caches
   */
  clearCache() {
    const keys = Object.keys(localStorage)
      .filter(key => key.startsWith('content_') || key.startsWith('cms_'));
    keys.forEach(key => localStorage.removeItem(key));
    sessionStorage.removeItem('cms_sections_cache');
    console.log(`[contentService] Cache cleared (${keys.length} items)`);
  }

  /**
   * Invalidate specific menu cache
   */
  invalidateMenu(menu) {
    const keys = Object.keys(localStorage)
      .filter(key => key.includes(menu));
    keys.forEach(key => localStorage.removeItem(key));
    console.log(`[contentService] Menu cache invalidated: ${menu}`);
  }
}

export default new ContentService();
